import React from 'react';
import { Alert, getFaIcon } from '@odin-labs/components';
import {
  CardPrintJobStatus,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { BadgingStep, CardPrintJob } from 'containers/worker/types';
import { AuthContext } from 'auth';
import { to } from 'acl';
import {
  faIdCard,
  faPrint,
  ArrowRightIcon,
  MinusOctagonIcon,
  RedoIcon,
  UserPlusIcon,
} from 'components/icons';

export type WorkerBadgesAlertsProps = {
  currentCardPrintJob: CardPrintJob;
  openBadgeModal: (badgingStep: BadgingStep) => void;
  hasExtraActiveCards: boolean;
  hasExtraActiveQRCodes: boolean;
};

const isCardPrintJobUnread = (cardPrintJob: CardPrintJob): boolean =>
  cardPrintJob?.status === CardPrintJobStatus.Printed &&
  cardPrintJob?.completion?.cardNumber == null &&
  cardPrintJob?.workerCard == null;

const IdCardIcon = getFaIcon({
  icon: faIdCard,
  className: 'odin-mr-3 !odin-leading-5',
});
const PrintIcon = getFaIcon({
  icon: faPrint,
  className: 'odin-mr-3 !odin-leading-5',
});

export function WorkerBadgesAlerts(
  props: WorkerBadgesAlertsProps,
): React.ReactElement {
  const {
    currentCardPrintJob,
    openBadgeModal,
    hasExtraActiveCards,
    hasExtraActiveQRCodes,
  } = props;

  const { currentUser: user } = React.useContext(AuthContext);
  const canAddBadges = user.isAllowed(to.addBadges);
  const canDeactivateBadges = user.isAllowed(to.deactivateBadges);
  const isPvcCard =
    currentCardPrintJob?.workerCard?.workerCardFormat?.cardType ===
    WorkerCardType.Pvc;

  return (
    <>
      {
        // for pvc cards, reprinting will be handled within the table row for that card,
        // so we only show the alert for failed print jobs that are not assigned to a pvc card
        !isPvcCard && currentCardPrintJob?.status === CardPrintJobStatus.Failed && (
          <Alert
            type="danger"
            text="Your badge failed to print"
            {...(canAddBadges
              ? {
                  onAction: (): void => {
                    openBadgeModal(BadgingStep.CreatePrintJob);
                  },
                  actionText: 'Try Again',
                  actionIcon: RedoIcon,
                }
              : undefined)}
          />
        )
      }
      {isCardPrintJobUnread(currentCardPrintJob) && (
        <Alert
          text={
            'A badge was printed for this worker, but not registered. ' +
            'Please enter the number printed on the badge to register it.'
          }
          icon={IdCardIcon}
          {...(canAddBadges
            ? {
                onAction: (): void => {
                  openBadgeModal(BadgingStep.PrintCompletedNoCardNumber);
                },
                actionText: 'Register Badge',
                actionIcon: UserPlusIcon,
              }
            : undefined)}
        />
      )}
      {currentCardPrintJob?.status === CardPrintJobStatus.Submitted && (
        <Alert
          text="A badge is currently printing for this worker"
          icon={PrintIcon}
          {...(canAddBadges
            ? {
                onAction: (): void => {
                  openBadgeModal(BadgingStep.Printing);
                },
                actionText: 'Resume',
                actionIcon: ArrowRightIcon,
              }
            : undefined)}
        />
      )}
      {hasExtraActiveCards && (
        <Alert
          text={
            'This worker has multiple active badges. ' +
            'We recommend deactivating all but one active badge for security purposes.'
          }
          {...(canDeactivateBadges
            ? {
                onAction: (): void => {
                  openBadgeModal(BadgingStep.ProximityCardDeactivationPrompt);
                },
                actionText: 'Deactivate',
                actionIcon: MinusOctagonIcon,
              }
            : undefined)}
        />
      )}
      {hasExtraActiveQRCodes && (
        <Alert
          text={
            'This worker has multiple active QR Codes. ' +
            'We recommend deactivating all but one active badge for security purposes.'
          }
          {...(canDeactivateBadges
            ? {
                onAction: (): void => {
                  openBadgeModal(BadgingStep.QRCodeDeactivationPrompt);
                },
                actionText: 'Deactivate',
                actionIcon: MinusOctagonIcon,
              }
            : undefined)}
        />
      )}
    </>
  );
}
